import { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/common/Button';
import { platformMdas, type MDA, type BulkImportResult as PlatformBulkImportResult } from '@/services/platform';
import styles from './BulkImportModal.module.css';

interface PlatformBulkImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface PlatformBulkImportUserRow {
  mdaCode: string;
  firstName: string;
  lastName: string;
  email: string;
  staffId?: string | null;
  jobTitle?: string | null;
  phone?: string | null;
  departmentCode?: string | null;
  securityClearance?: number;
  roleCode?: string | null;
}

interface ParsedRow extends PlatformBulkImportUserRow {
  _rowNum: number;
  _errors: string[];
  _warnings: string[];
}

type ImportStep = 'upload' | 'preview' | 'importing' | 'results';

export function PlatformBulkImportModal({ isOpen, onClose, onSuccess }: PlatformBulkImportModalProps) {
  const [step, setStep] = useState<ImportStep>('upload');
  const [parsedData, setParsedData] = useState<ParsedRow[]>([]);
  const [mdas, setMdas] = useState<MDA[]>([]);
  const [importResult, setImportResult] = useState<PlatformBulkImportResult | null>(null);
  const [, setImporting] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);

  // Reset state when modal closes
  const handleClose = () => {
    setStep('upload');
    setParsedData([]);
    setImportResult(null);
    setParseError(null);
    onClose();
  };

  // Download template
  const downloadTemplate = () => {
    const templateData = [
      {
        mdaCode: 'OHCS',
        firstName: 'John',
        lastName: 'Doe',
        email: 'john.doe@ohcs.gov.gh',
        staffId: 'GCS-001',
        jobTitle: 'Senior Analyst',
        phone: '+233200000001',
        departmentCode: 'HR',
        securityClearance: 1,
        roleCode: 'STAFF',
      },
      {
        mdaCode: 'MOF',
        firstName: 'Jane',
        lastName: 'Smith',
        email: 'jane.smith@mof.gov.gh',
        staffId: 'MOF-002',
        jobTitle: 'Finance Officer',
        phone: '+233200000002',
        departmentCode: 'FINANCE',
        securityClearance: 2,
        roleCode: 'RECORDS_OFFICER',
      },
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);

    // Set column widths
    ws['!cols'] = [
      { wch: 12 }, // mdaCode
      { wch: 15 }, // firstName
      { wch: 15 }, // lastName
      { wch: 30 }, // email
      { wch: 12 }, // staffId
      { wch: 25 }, // jobTitle
      { wch: 15 }, // phone
      { wch: 15 }, // departmentCode
      { wch: 18 }, // securityClearance
      { wch: 20 }, // roleCode
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Staff Import');

    // Add instructions sheet
    const instructions = [
      ['OHCS Platform Staff Import Template'],
      [''],
      ['Instructions:'],
      ['1. Fill in the staff data in the "Staff Import" sheet'],
      ['2. Required fields: mdaCode, firstName, lastName, email'],
      ['3. mdaCode must match an existing institution code in the platform'],
      ['4. Optional fields: staffId, jobTitle, phone, departmentCode, securityClearance, roleCode'],
      ['5. securityClearance must be a number between 1-5 (default: 1)'],
      ['6. departmentCode must match an existing department code within the specified MDA'],
      ['7. roleCode must match an existing role code within the specified MDA'],
      ['8. Passwords will be auto-generated for each user'],
      [''],
      ['Field Descriptions:'],
      ['mdaCode - Institution code (e.g., OHCS, MOF) (required)'],
      ['firstName - Staff first name (required)'],
      ['lastName - Staff last name (required)'],
      ['email - Staff email address (required, must be unique within MDA)'],
      ['staffId - Staff identification number'],
      ['jobTitle - Staff job title'],
      ['phone - Phone number'],
      ['departmentCode - Code of the department to assign staff to'],
      ['securityClearance - Security level 1-5 (1=lowest, 5=highest)'],
      ['roleCode - Code of the role to assign to staff'],
    ];

    const wsInstructions = XLSX.utils.aoa_to_sheet(instructions);
    wsInstructions['!cols'] = [{ wch: 80 }];
    XLSX.utils.book_append_sheet(wb, wsInstructions, 'Instructions');

    XLSX.writeFile(wb, 'OHCS_Platform_Staff_Import_Template.xlsx');
  };

  // Fetch MDAs for validation
  const fetchMdas = useCallback(async () => {
    try {
      const response = await platformMdas.list({ perPage: 500 });
      if (response.success && response.data) {
        setMdas(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch MDAs:', err);
    }
  }, []);

  // Handle file upload
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setParseError(null);

    // Fetch MDAs for validation
    await fetchMdas();

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);

      // Get the first sheet (Staff Import)
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];

      // Parse to JSON
      const jsonData = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet);

      if (jsonData.length === 0) {
        setParseError('The uploaded file contains no data rows.');
        return;
      }

      if (jsonData.length > 500) {
        setParseError('Maximum 500 rows allowed per import. Please split your file.');
        return;
      }

      // Create MDA map for validation
      const mdaCodes = new Set(mdas.filter(m => m.isActive).map(m => m.code.toUpperCase()));
      const emails = new Set<string>();

      // Validate and transform data
      const parsed: ParsedRow[] = jsonData.map((row, index) => {
        const errors: string[] = [];
        const warnings: string[] = [];

        const mdaCode = String(row.mdaCode || '').trim().toUpperCase();
        const firstName = String(row.firstName || '').trim();
        const lastName = String(row.lastName || '').trim();
        const email = String(row.email || '').trim().toLowerCase();
        const staffId = row.staffId ? String(row.staffId).trim() : null;
        const jobTitle = row.jobTitle ? String(row.jobTitle).trim() : null;
        const phone = row.phone ? String(row.phone).trim() : null;
        const departmentCode = row.departmentCode ? String(row.departmentCode).trim().toUpperCase() : null;
        const securityClearance = row.securityClearance ? Number(row.securityClearance) : 1;
        const roleCode = row.roleCode ? String(row.roleCode).trim().toUpperCase() : null;

        // Validate required fields
        if (!mdaCode) {
          errors.push('MDA code is required');
        } else if (!mdaCodes.has(mdaCode)) {
          errors.push(`MDA code '${mdaCode}' not found or inactive`);
        }
        if (!firstName) errors.push('First name is required');
        if (!lastName) errors.push('Last name is required');
        if (!email) {
          errors.push('Email is required');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          errors.push('Invalid email format');
        } else if (emails.has(email)) {
          errors.push('Duplicate email in file');
        }
        emails.add(email);

        // Validate optional fields
        if (securityClearance < 1 || securityClearance > 5) {
          errors.push('Security clearance must be 1-5');
        }

        // Warnings (can't validate department/role codes without fetching from each MDA)
        if (!staffId) warnings.push('No staff ID provided');
        if (!departmentCode) warnings.push('No department assigned');
        if (departmentCode) warnings.push('Department code will be validated on import');
        if (roleCode) warnings.push('Role code will be validated on import');

        return {
          _rowNum: index + 2, // Excel row number (1-indexed + header)
          _errors: errors,
          _warnings: warnings,
          mdaCode,
          firstName,
          lastName,
          email,
          staffId,
          jobTitle,
          phone,
          departmentCode,
          securityClearance,
          roleCode,
        };
      });

      setParsedData(parsed);
      setStep('preview');
    } catch (err) {
      console.error('Parse error:', err);
      setParseError('Failed to parse the Excel file. Please ensure it is a valid .xlsx file.');
    }

    // Reset file input
    e.target.value = '';
  };

  // Execute import
  const executeImport = async () => {
    const validRows = parsedData.filter(row => row._errors.length === 0);

    if (validRows.length === 0) {
      return;
    }

    setImporting(true);
    setStep('importing');

    try {
      const response = await platformMdas.bulkImportUsers({
        users: validRows.map(row => ({
          mdaCode: row.mdaCode,
          firstName: row.firstName,
          lastName: row.lastName,
          email: row.email,
          staffId: row.staffId,
          jobTitle: row.jobTitle,
          phone: row.phone,
          departmentCode: row.departmentCode,
          securityClearance: row.securityClearance,
          roleCode: row.roleCode,
        })),
        generatePasswords: true,
        sendWelcomeEmail: false,
      });

      if (response.data) {
        setImportResult(response.data);
        setStep('results');

        if (response.data.successCount > 0) {
          onSuccess();
        }
      }
    } catch (err) {
      console.error('Import failed:', err);
      setImportResult({
        success: false,
        totalProcessed: validRows.length,
        successCount: 0,
        failureCount: validRows.length,
        results: [],
      });
      setStep('results');
    } finally {
      setImporting(false);
    }
  };

  // Count stats
  const validCount = parsedData.filter(r => r._errors.length === 0).length;
  const errorCount = parsedData.filter(r => r._errors.length > 0).length;

  // Group by MDA for preview
  const mdaGroups = parsedData.reduce((acc, row) => {
    if (!acc[row.mdaCode]) acc[row.mdaCode] = [];
    acc[row.mdaCode].push(row);
    return acc;
  }, {} as Record<string, ParsedRow[]>);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Platform Bulk Import Staff"
      size="lg"
    >
      <div className={styles.container}>
        {/* Step indicator */}
        <div className={styles.steps}>
          <div className={`${styles.step} ${step === 'upload' ? styles.active : ''} ${['preview', 'importing', 'results'].includes(step) ? styles.completed : ''}`}>
            <span className={styles.stepNum}>1</span>
            <span className={styles.stepLabel}>Upload</span>
          </div>
          <div className={styles.stepLine} />
          <div className={`${styles.step} ${step === 'preview' ? styles.active : ''} ${['importing', 'results'].includes(step) ? styles.completed : ''}`}>
            <span className={styles.stepNum}>2</span>
            <span className={styles.stepLabel}>Preview</span>
          </div>
          <div className={styles.stepLine} />
          <div className={`${styles.step} ${step === 'importing' ? styles.active : ''} ${step === 'results' ? styles.completed : ''}`}>
            <span className={styles.stepNum}>3</span>
            <span className={styles.stepLabel}>Import</span>
          </div>
        </div>

        {/* Upload Step */}
        {step === 'upload' && (
          <div className={styles.uploadStep}>
            <div className={styles.templateSection}>
              <h3>Step 1: Download Template</h3>
              <p>Download the Excel template and fill in your staff data. Include the MDA code for each row.</p>
              <Button variant="secondary" onClick={downloadTemplate}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
                </svg>
                Download Platform Template
              </Button>
            </div>

            <div className={styles.uploadSection}>
              <h3>Step 2: Upload Completed File</h3>
              <p>Upload your filled Excel file (.xlsx) to preview and import across multiple institutions.</p>

              <label className={styles.uploadArea}>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  hidden
                />
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                </svg>
                <span className={styles.uploadText}>Click to upload or drag and drop</span>
                <span className={styles.uploadHint}>.xlsx or .xls (max 500 rows)</span>
              </label>

              {parseError && (
                <div className={styles.error}>{parseError}</div>
              )}
            </div>
          </div>
        )}

        {/* Preview Step */}
        {step === 'preview' && (
          <div className={styles.previewStep}>
            <div className={styles.previewStats}>
              <div className={styles.statCard}>
                <span className={styles.statValue}>{parsedData.length}</span>
                <span className={styles.statLabel}>Total Rows</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statValue}>{Object.keys(mdaGroups).length}</span>
                <span className={styles.statLabel}>Institutions</span>
              </div>
              <div className={`${styles.statCard} ${styles.success}`}>
                <span className={styles.statValue}>{validCount}</span>
                <span className={styles.statLabel}>Valid</span>
              </div>
              {errorCount > 0 && (
                <div className={`${styles.statCard} ${styles.error}`}>
                  <span className={styles.statValue}>{errorCount}</span>
                  <span className={styles.statLabel}>Errors</span>
                </div>
              )}
            </div>

            <div className={styles.previewTable}>
              <table>
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Status</th>
                    <th>MDA</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Staff ID</th>
                    <th>Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedData.map((row) => (
                    <tr key={row._rowNum} className={row._errors.length > 0 ? styles.errorRow : row._warnings.length > 0 ? styles.warningRow : ''}>
                      <td>{row._rowNum}</td>
                      <td>
                        {row._errors.length > 0 ? (
                          <span className={styles.statusError}>Error</span>
                        ) : row._warnings.length > 0 ? (
                          <span className={styles.statusWarning}>Warning</span>
                        ) : (
                          <span className={styles.statusValid}>Valid</span>
                        )}
                      </td>
                      <td>{row.mdaCode || '-'}</td>
                      <td>{row.firstName} {row.lastName}</td>
                      <td>{row.email}</td>
                      <td>{row.staffId || '-'}</td>
                      <td>
                        {row._errors.length > 0 && (
                          <span className={styles.issues}>{row._errors.join('; ')}</span>
                        )}
                        {row._warnings.length > 0 && row._errors.length === 0 && (
                          <span className={styles.issuesWarning}>{row._warnings.slice(0, 2).join('; ')}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={styles.previewActions}>
              <Button variant="secondary" onClick={() => setStep('upload')}>
                Back
              </Button>
              <Button
                onClick={executeImport}
                disabled={validCount === 0}
              >
                Import {validCount} Staff {errorCount > 0 && `(${errorCount} will be skipped)`}
              </Button>
            </div>
          </div>
        )}

        {/* Importing Step */}
        {step === 'importing' && (
          <div className={styles.importingStep}>
            <div className={styles.spinner} />
            <h3>Importing Staff...</h3>
            <p>Please wait while we create staff records and file jackets across institutions.</p>
          </div>
        )}

        {/* Results Step */}
        {step === 'results' && importResult && (
          <div className={styles.resultsStep}>
            <div className={`${styles.resultsSummary} ${importResult.success ? styles.success : styles.partial}`}>
              {importResult.success ? (
                <>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <h3>Import Completed Successfully!</h3>
                </>
              ) : (
                <>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <h3>Import Completed with Issues</h3>
                </>
              )}
              <div className={styles.resultsStats}>
                <span className={styles.successStat}>{importResult.successCount} imported</span>
                {importResult.failureCount > 0 && (
                  <span className={styles.failStat}>{importResult.failureCount} failed</span>
                )}
              </div>
            </div>

            {importResult.results.some(r => !r.success) && (
              <div className={styles.failedRows}>
                <h4>Failed Rows</h4>
                <table>
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Email</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importResult.results.filter(r => !r.success).map((r) => (
                      <tr key={r.row}>
                        <td>{r.row}</td>
                        <td>{r.email}</td>
                        <td>{r.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className={styles.resultsActions}>
              <Button onClick={handleClose}>
                Done
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
